import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';

const BookPrinting = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const services = [
    {
      title: 'Offset Printing',
      description: 'High-volume printing with superior quality and cost efficiency for large print runs of 500+ copies.',
      features: ['Best for 500+ copies', 'Premium paper quality', 'Full color CMYK', 'Hardcover & paperback']
    },
    {
      title: 'Print-on-Demand',
      description: 'Print books as ordered with no inventory risk. Perfect for self-publishers and small runs.',
      features: ['No minimum order', 'Single copy printing', 'Multiple retailers', 'Global fulfillment']
    },
    {
      title: 'Hardcover Printing',
      description: 'Premium hardcover books with dust jackets, case binding, and luxury finishing options.',
      features: ['Smyth sewn binding', 'Dust jacket options', 'Premium materials', 'Foil stamping available']
    },
    {
      title: 'Specialty Finishing',
      description: 'Embossing, foil stamping, spot UV, and other premium finishing options to make your book stand out.',
      features: ['Foil stamping', 'Embossing/debossing', 'Spot UV coating', 'Custom end papers']
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Quote & Specifications',
      description: 'We discuss your printing needs including quantity, size, paper type, binding, and finishing. You receive a detailed quote with multiple paper and binding options.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524659.png'
    },
    {
      step: '02',
      title: 'File Preparation & Proofing',
      description: 'Your files are prepared for printing with proper bleed, margins, and color profiles. You receive and approve a digital proof before printing begins.',
      icon: 'https://cdn-icons-png.flaticon.com/512/2521/2521826.png'
    },
    {
      step: '03',
      title: 'Printing & Delivery',
      description: 'Books are printed, bound, and quality-checked. Finished books are shipped directly to you or your specified location with tracking.',
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524388.png'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3330/3330315.png',
      title: 'Premium Quality',
      description: 'Industry-leading print quality with color accuracy and attention to detail.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2490/2490402.png',
      title: 'Competitive Pricing',
      description: 'Direct relationships with printers ensure the best prices without compromising quality.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/2838/2838779.png',
      title: 'Fast Turnaround',
      description: 'Most print jobs completed within 2-3 weeks including shipping.'
    },
    {
      icon: 'https://cdn-icons-png.flaticon.com/512/3524/3524715.png',
      title: 'Quality Guarantee',
      description: 'Every book is inspected. We reprint at no charge if quality standards aren\'t met.'
    }
  ];

  const faqs = [
    {
      question: 'What is the minimum order quantity?',
      answer: 'For offset printing, minimum is typically 500 copies for best pricing, though we can do runs as small as 250 copies. Print-on-demand has no minimum - you can order as few as 1 copy. We\'ll recommend the most cost-effective option based on your needs.'
    },
    {
      question: 'How much does book printing cost?',
      answer: 'Costs vary by quantity, size, page count, color vs. B&W interior, and binding type. Typical pricing: 200-page paperback POD: $4-6/book. 500-copy offset run: $3-4/book. 5,000-copy offset run: $2-2.50/book. Hardcover adds $3-5 per book. We provide detailed quotes.'
    },
    {
      question: 'What\'s the difference between offset and print-on-demand?',
      answer: 'Offset printing uses traditional printing plates and is cost-effective for 500+ copies with superior quality. Print-on-demand prints digitally as orders come in - higher per-unit cost but no upfront inventory investment. We recommend POD for under 500 copies, offset for larger quantities.'
    },
    {
      question: 'What paper types and binding options are available?',
      answer: 'Paper: Cream or white offset paper (50#, 60#, 70# weight), glossy or matte coated stock for photo books. Binding: Perfect bound paperback, case bound hardcover, Smyth sewn hardcover (premium), saddle stitch for thin books. We\'ll recommend based on your book type and budget.'
    },
    {
      question: 'How long does printing take?',
      answer: 'Print-on-demand: 3-5 business days + shipping. Offset printing: 2-3 weeks including printing, binding, and shipping. Rush printing available for urgent projects at additional cost. International shipping adds 1-2 weeks.'
    },
    {
      question: 'Do you ship internationally?',
      answer: 'Yes, we ship worldwide. For US authors, we recommend US printing for fastest delivery and lower shipping costs. For international authors, we can arrange printing in your region or ship from the US. Customs and duties may apply for international shipments.'
    },
    {
      question: 'Can I see a sample before printing my full order?',
      answer: 'Yes! For offset printing orders, we highly recommend ordering a sample first. We can print 1-5 sample copies using the same paper and binding as your final run so you can verify quality before committing to the full print run.'
    },
    {
      question: 'What file formats do you need for printing?',
      answer: 'We accept print-ready PDF files with proper bleed (0.125" on all sides), embedded fonts, and CMYK color mode (not RGB). We can prepare your files if they\'re not print-ready. InDesign, Word, and other formats can be converted to print-ready PDFs for a small fee.'
    }
  ];

  const testimonials = [
    {
      name: 'Richard Palmer',
      title: 'Historical Fiction Author',
      text: 'Printed 2,000 copies of my novel. The quality is outstanding - better than some traditionally published books. The price was very competitive too.',
      rating: 5
    },
    {
      name: 'Christine Moore',
      title: 'Children\'s Book Author',
      text: 'The hardcover printing with foil stamping on the cover turned out beautifully. The team walked me through every option. Highly recommend!',
      rating: 5
    },
    {
      name: 'James Wilson',
      title: 'Business Book Author',
      text: 'Used POD initially, then switched to offset printing when sales took off. The guidance on when to make that switch saved me thousands of dollars.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white pt-24 py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PROFESSIONAL BOOK PRINTING</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Premium Book Printing at <span className="text-amber-700">Wholesale Prices</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Professional offset and print-on-demand services with superior quality and competitive pricing. From single copies to large print runs, we deliver books that exceed expectations.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Button 
                  onClick={() => setIsModalOpen(true)}
                  className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
                >
                  Get Printing Quote
                </Button>
                <Button 
                  onClick={() => scrollToSection('process')}
                  className="bg-white hover:bg-gray-50 text-amber-700 border-2 border-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  See How It Works
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">5M+</div>
                  <div className="text-gray-600 text-sm">Books Printed</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">250+</div>
                  <div className="text-gray-600 text-sm">Min Offset Run</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">100%</div>
                  <div className="text-gray-600 text-sm">Quality Guaranteed</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-amber-700 mb-2">2-3</div>
                  <div className="text-gray-600 text-sm">Weeks Average</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=800&h=600&fit=crop" 
                  alt="Professional book printing"
                  className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                      <img 
                        src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                        alt="Books printed"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">5M+</div>
                      <div className="text-sm text-gray-600">Books Printed</div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full opacity-20 blur-2xl"></div>
              <div className="absolute -bottom-6 right-12 w-40 h-40 bg-amber-300 rounded-full opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Offset vs POD Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <img 
                src="https://images.unsplash.com/photo-1507914372368-b2b085b925a1?w=800&h=600&fit=crop" 
                alt="Printing press"
                className="rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-amber-700/20 to-transparent rounded-2xl"></div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">PRINTING OPTIONS</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Offset & Print-on-Demand <span className="text-amber-700">Services</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Whether you need 1 copy or 10,000 copies, we have the right printing solution for your project and budget.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/3330/3330315.png" 
                      alt="Offset printing"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Offset Printing (500+ copies)</h3>
                    <p className="text-gray-600">Superior quality, lower per-unit cost. Best for established books with predictable demand.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/2521/2521826.png" 
                      alt="Print-on-demand"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Print-on-Demand (1+ copies)</h3>
                    <p className="text-gray-600">No inventory risk, single copy fulfillment. Perfect for new releases and testing the market.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center p-3">
                    <img 
                      src="https://cdn-icons-png.flaticon.com/512/1087/1087927.png" 
                      alt="Hybrid approach"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Hybrid Approach</h3>
                    <p className="text-gray-600">Start with POD, transition to offset as sales grow. We help you time the switch perfectly.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={() => setIsModalOpen(true)}
                className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-6 px-10 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
              >
                Compare Printing Options
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Creative CTA */}
      <section className="py-20 bg-gradient-to-br from-amber-700 via-amber-600 to-amber-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Print Quality Matters
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Readers notice print quality. Professional printing with premium paper and binding creates books that look and feel like traditionally published titles.
            </p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-10 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Request Sample Book
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Complete <span className="text-amber-700">Printing Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From paperback to premium hardcover with specialty finishing options.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-amber-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
              <span className="text-amber-700 text-sm font-semibold tracking-wider">OUR PROCESS</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It <span className="text-amber-700">Works</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A streamlined printing process from quote to delivered books at your doorstep.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-gradient-to-r from-amber-200 via-amber-400 to-amber-200" style={{ top: '80px' }}></div>

              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 h-full border-t-4 border-amber-500 relative z-10">
                    <div className="absolute -top-6 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform">
                        <span className="text-2xl font-bold text-white">{item.step}</span>
                      </div>
                    </div>

                    <div className="mt-8 mb-6 flex justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-amber-50 to-amber-100 rounded-full flex items-center justify-center p-5">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed text-center">{item.description}</p>

                    {index < process.length - 1 && (
                      <div className="hidden md:block absolute -right-4 top-20 text-amber-500">
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>

                  {index < process.length - 1 && (
                    <div className="md:hidden flex justify-center py-4">
                      <svg className="w-8 h-8 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v10.586l3.293-3.293a1 1 0 111.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 111.414-1.414L9 14.586V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="text-center mt-16">
            <p className="text-gray-600 mb-6 text-lg">Ready to print your book?</p>
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white font-bold py-6 px-12 text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all"
            >
              Get Your Free Quote
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
                <span className="text-amber-700 text-sm font-semibold tracking-wider">WHY CHOOSE US</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Choose <span className="text-amber-700">Rushmore Publishing</span>
              </h2>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Industry-leading print quality with competitive wholesale pricing and personalized service.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center p-3">
                        <img 
                          src={item.icon} 
                          alt={item.title}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&h=600&fit=crop" 
                alt="Premium book printing"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-amber-700 text-white rounded-2xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold mb-2">5M+</div>
                <div className="text-sm">Books Printed</div>
                <div className="mt-3 text-xs opacity-90">Quality guaranteed on every copy</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=1920&h=800&fit=crop" 
            alt="Start printing"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-900/95 to-amber-700/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full mb-8">
              <span className="text-white text-sm font-semibold tracking-wider">FREE PRINTING QUOTE</span>
            </div>
            
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Print Your Book?
            </h2>
            <p className="text-xl mb-12 opacity-90 leading-relaxed">
              Get a free, detailed quote for your printing project. Whether it's 1 copy or 10,000 copies, we deliver exceptional quality at competitive prices.
            </p>
            
            <Button 
              onClick={() => setIsModalOpen(true)}
              className="bg-white hover:bg-gray-100 text-amber-700 font-bold py-6 px-12 text-xl rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
            >
              Get Your Free Quote
            </Button>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto pt-12 border-t border-white/20 mt-12">
              <div>
                <div className="text-3xl font-bold mb-2">5M+</div>
                <div className="text-sm opacity-80">Books Printed</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">250+</div>
                <div className="text-sm opacity-80">Min Offset Run</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-sm opacity-80">Quality Guaranteed</div>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <div className="text-sm opacity-80">Support Available</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-amber-700">Authors Say</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-amber-500 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-600">{testimonial.title}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-amber-700">Questions</span>
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="mb-4">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full bg-gray-50 hover:bg-gray-100 p-6 rounded-xl text-left flex items-center justify-between transition-all"
                >
                  <span className="font-bold text-gray-900 text-lg pr-4">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-amber-700 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="bg-white p-6 rounded-b-xl border-l-4 border-amber-700">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BookPrinting;
